import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import {
    Calendar,
    Cookie as CookieIcon,
    Image as ImageIcon,
    Images,
    LogOut,
    Mail,
    Megaphone,
    Settings,
    Sprout,
    Store,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const NAV = [
    { to: "/admin", label: "Tableau de bord", icon: Settings, exact: true },
    { to: "/admin/menu", label: "Carte (cookies/brownies/boissons)", icon: CookieIcon },
    { to: "/admin/events", label: "Évènements", icon: Calendar },
    { to: "/admin/popups", label: "Pop-ups", icon: Megaphone },
    { to: "/admin/shops", label: "Boutiques & horaires", icon: Store },
    { to: "/admin/ingredients", label: "Ingrédients", icon: Sprout },
    { to: "/admin/cookie-of-month", label: "Cookie du mois", icon: ImageIcon },
    { to: "/admin/numbercake-gallery", label: "Galerie Number Cake", icon: Images },
    { to: "/admin/cookie-perso-gallery", label: "Galerie Cookies perso", icon: Images },
    { to: "/admin/contacts", label: "Messages", icon: Mail },
];

export default function AdminLayout() {
    const { user, checked, logout } = useAuth();
    const location = useLocation();

    if (!checked) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-grain">
                <p className="font-handwritten text-3xl text-[#6B4226]">Chargement…</p>
            </div>
        );
    }
    if (!user) return <Navigate to="/admin/login" replace />;

    return (
        <div className="min-h-screen bg-[#F5E6D3] flex" data-testid="admin-shell">
            <aside className="w-72 hidden md:flex flex-col bg-[#FFF1E0] border-r border-[#E4D1BB] sticky top-0 h-screen">
                <Link to="/admin" className="flex items-center gap-3 px-6 h-20 border-b border-[#E4D1BB] shrink-0">
                    <img src="/brand/logo.png" alt="logo" className="h-12" />
                </Link>
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.exact}
                            data-testid={`admin-nav-${n.to.split("/").pop()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                                    isActive
                                        ? "bg-[#B07B66] text-white"
                                        : "text-[#3D1F0F] hover:bg-[#E4D1BB]/60"
                                }`
                            }
                        >
                            <n.icon size={16} strokeWidth={1.6} /> {n.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-[#E4D1BB] text-xs text-[#5C3D2E] shrink-0">
                    <p className="truncate">{user.email}</p>
                    <button
                        type="button"
                        onClick={logout}
                        className="mt-2 inline-flex items-center gap-2 text-[#6B4226] hover:text-[#3D1F0F]"
                        data-testid="admin-logout"
                    >
                        <LogOut size={14} /> Se déconnecter
                    </button>
                </div>
            </aside>

            <div className="flex-1 min-w-0">
                <header className="md:hidden flex items-center justify-between bg-[#FFF1E0] border-b border-[#E4D1BB] px-5 h-16">
                    <Link to="/admin" className="flex items-center gap-2">
                        <img src="/brand/logo.png" alt="logo" className="h-9" />
                    </Link>
                    <button
                        type="button"
                        onClick={logout}
                        className="text-[#6B4226]"
                        data-testid="admin-logout-mobile"
                    >
                        <LogOut size={18} />
                    </button>
                </header>
                <div className="md:hidden bg-[#FFF1E0] border-b border-[#E4D1BB] overflow-x-auto">
                    <nav className="flex gap-2 px-3 py-3 text-sm whitespace-nowrap">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.exact}
                                className={({ isActive }) =>
                                    `px-3 py-1.5 rounded-full ${
                                        isActive
                                            ? "bg-[#B07B66] text-white"
                                            : "text-[#3D1F0F] bg-white border border-[#E4D1BB]"
                                    }`
                                }
                            >
                                {n.label.split(" ")[0]}
                            </NavLink>
                        ))}
                    </nav>
                </div>
                <main className="p-6 md:p-10" key={location.pathname}>
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
